import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import {
  Banknote, TrendingUp, AlertTriangle, CheckCircle2, RefreshCw, CalendarClock, Zap,
} from 'lucide-react';

const money = (cents) =>
  `$${((cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * SweepPreview widget — drops into the existing /clinician/banking or
 * /clinician/payouts page. Pulls /api/clinician/sweep-preview and renders:
 *
 *   • Next sweep date (auto-Friday 18:00 UTC)
 *   • 4 KPI tiles (gross / fee / net / instant-payout option)
 *   • Connect readiness pill (red if onboarding incomplete)
 *   • Per-visit table (date, initials, gross→fee→net)
 *
 * Reusable as a card; the host page passes optional `compact` and the
 * widget loads on mount.
 */
export const SweepPreview = ({ getAuthHeader, compact = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const { data } = await axios.get(`${API}/clinician/sweep-preview`, getAuthHeader());
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not load sweep preview');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return <div className="cn-card h-40 animate-pulse" data-testid="sweep-preview-loading" />;
  }
  if (err) {
    return (
      <div className="cn-card p-6 border-amber-200 bg-amber-50/30" data-testid="sweep-preview-error">
        <div className="flex items-center gap-2 text-amber-700">
          <AlertTriangle className="w-4 h-4" />
          <span className="text-sm font-semibold">Sweep preview unavailable</span>
        </div>
        <p className="text-xs text-cn-text-muted mt-1">{err}</p>
      </div>
    );
  }

  const nextSweepDate = data?.next_sweep_at ? new Date(data.next_sweep_at) : null;
  const nextSweepLabel = nextSweepDate
    ? nextSweepDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })
    : '—';
  const connectReady = data?.connect_status?.ready_to_sweep;
  const instant = data?.instant_payout || {};

  return (
    <section className="cn-card p-6" data-testid="sweep-preview">
      {/* HEADER */}
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <div className="cn-eyebrow mb-1 inline-flex items-center gap-1.5">
            <CalendarClock className="w-3.5 h-3.5" /> Next auto-sweep
          </div>
          <h2 className="font-display font-bold text-xl text-cn-text dark:text-white" data-testid="sweep-next-date">
            {nextSweepLabel}
          </h2>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {connectReady ? (
            <span className="cn-trust-badge text-emerald-700 border-emerald-200 bg-emerald-50" data-testid="sweep-connect-ready">
              <CheckCircle2 className="w-3 h-3" /> Connect ready
            </span>
          ) : (
            <span className="cn-trust-badge text-red-700 border-red-200 bg-red-50" data-testid="sweep-connect-not-ready">
              <AlertTriangle className="w-3 h-3" /> Connect onboarding incomplete
            </span>
          )}
          <button
            type="button"
            onClick={load}
            className="cn-btn cn-btn-ghost !py-1.5 !px-2"
            aria-label="Refresh"
            data-testid="sweep-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* KPI STRIP */}
      <div className="grid grid-cols-4 gap-3 mb-5" data-testid="sweep-kpis">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">Eligible visits</div>
          <div className="font-display font-bold text-2xl text-cn-text mt-0.5" data-testid="sweep-eligible-count">{data?.eligible_visit_count ?? 0}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">Gross</div>
          <div className="font-display font-bold text-2xl text-cn-text mt-0.5" data-testid="sweep-gross">{money(data?.total_gross_cents)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700">Platform fee ({Math.round((data?.platform_fee_pct || 0) * 100)}%)</div>
          <div className="font-display font-bold text-2xl text-amber-700 mt-0.5" data-testid="sweep-fee">{money(data?.total_platform_fee_cents)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">Net to bank</div>
          <div className="font-display font-bold text-2xl text-emerald-700 mt-0.5 inline-flex items-center gap-1.5" data-testid="sweep-net">
            <Banknote className="w-5 h-5" /> {money(data?.total_net_cents)}
          </div>
        </div>
      </div>

      {/* INSTANT-PAYOUT OPTION */}
      {data?.total_net_cents > 0 && instant.available && (
        <div className="rounded-xl border border-cn-primary/30 bg-cn-primary/5 p-4 mb-5 flex items-center gap-3" data-testid="sweep-instant-option">
          <Zap className="w-5 h-5 text-cn-primary shrink-0" />
          <div className="flex-1">
            <div className="font-semibold text-sm text-cn-text">Get paid today (instant payout)</div>
            <div className="text-xs text-cn-text-muted mt-0.5">
              Pay a {Math.round((instant.fee_pct || 0) * 1000) / 10}% Stripe fee ({money(instant.fee_cents)}) and receive{' '}
              <strong className="text-cn-primary">{money(instant.net_cents)}</strong> within 30 minutes instead of {nextSweepLabel}.
            </div>
          </div>
        </div>
      )}

      {/* PER-VISIT TABLE */}
      {!compact && (data?.items?.length > 0 ? (
        <div className="cn-card overflow-hidden" data-testid="sweep-items-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-cn-border bg-cn-bone-100/40">
                  <th className="text-left px-4 py-2.5 font-semibold text-cn-text-muted text-[11px] uppercase tracking-wider">Date</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-cn-text-muted text-[11px] uppercase tracking-wider">Patient</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-cn-text-muted text-[11px] uppercase tracking-wider">Type</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-cn-text-muted text-[11px] uppercase tracking-wider">Gross</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-cn-text-muted text-[11px] uppercase tracking-wider">Fee</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-cn-text-muted text-[11px] uppercase tracking-wider">Net</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((it, i) => (
                  <tr key={it.appointment_id || i} className={`border-b border-cn-border last:border-0 ${i % 2 === 1 ? 'bg-cn-bone-50/30' : ''}`} data-testid={`sweep-item-${i}`}>
                    <td className="px-4 py-2.5 text-cn-text-muted font-mono text-xs">{it.appointment_date || '—'}</td>
                    <td className="px-4 py-2.5 font-semibold text-cn-text">{it.patient_initials}</td>
                    <td className="px-4 py-2.5 text-cn-text-muted">{it.service_type || '—'}</td>
                    <td className="px-4 py-2.5 text-right text-cn-text">{money(it.gross_cents)}</td>
                    <td className="px-4 py-2.5 text-right text-amber-700">{money(it.fee_cents)}</td>
                    <td className="px-4 py-2.5 text-right text-emerald-700 font-semibold">{money(it.net_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="cn-card p-6 text-center text-sm text-cn-text-muted bg-cn-bone-100/30" data-testid="sweep-items-empty">
          <TrendingUp className="w-8 h-8 text-cn-text-muted/40 mx-auto mb-2" />
          No completed-unpaid visits queued for the next sweep yet. Complete a visit and it&rsquo;ll land here.
        </div>
      ))}
    </section>
  );
};

export default SweepPreview;
